import { useMemo, useState } from "react";
import { apiPost, queryClient } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmailComposer } from "@/components/shared/email-composer";
import { isAustralia, type CountryConfig } from "@/lib/country";

export type ReminderTemplate =
  | "upload_receipts"
  | "bas_reminder"
  | "tax_time"
  | "custom";

// D2f + D2n — human-readable template labels (not internal names)
export const REMINDER_TEMPLATE_LABELS: Record<ReminderTemplate, string> = {
  upload_receipts: "Upload receipts",
  bas_reminder: "BAS reminder (Australia)",
  tax_time: "Tax time",
  custom: "Custom message",
};

// Template-specific default subjects. The backend renders the same on send
// when no `subject_override` is sent, but surfacing them here gives the
// accountant a visible starting point they can edit (F22/F23).
const DEFAULT_SUBJECT: Record<ReminderTemplate, string> = {
  upload_receipts: "Time to upload your receipts",
  bas_reminder: "BAS lodgement reminder",
  tax_time: "Tax time is coming up",
  custom: "A message from your accountant",
};

/**
 * Templates that make sense for this country. BAS is an Australian lodgement,
 * so the BAS reminder is only offered for Australia; everything else works
 * anywhere (the "Tax time" email names the country's own tax year).
 */
function reminderTemplatesFor(
  config: CountryConfig | null | undefined,
): ReminderTemplate[] {
  const all = Object.keys(REMINDER_TEMPLATE_LABELS) as ReminderTemplate[];
  return isAustralia(config) ? all : all.filter((t) => t !== "bas_reminder");
}

interface ReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Either single client id (string) or bulk list of ids */
  target:
    | { kind: "single"; id: string; name: string }
    | { kind: "bulk"; ids: string[] };
  /** Decides which templates are offered: the client's country for a single
   *  client, the accountant's for a bulk send. */
  countryConfig?: CountryConfig | null;
  onSent?: () => void;
}

export function ReminderDialog({
  open,
  onOpenChange,
  target,
  countryConfig,
  onSent,
}: ReminderDialogProps) {
  const templates = reminderTemplatesFor(countryConfig);
  const [template, setTemplate] = useState<ReminderTemplate>("upload_receipts");
  const [sending, setSending] = useState(false);

  const defaultSubject = useMemo(() => DEFAULT_SUBJECT[template], [template]);

  const handleSend = async ({
    subject,
    body,
  }: {
    subject: string;
    body: string;
  }) => {
    setSending(true);
    try {
      if (target.kind === "single") {
        await apiPost(`/api/accountant/users/${target.id}/remind`, {
          template,
          custom_message: body,
          subject,
        });
        toast.success(`Reminder sent to ${target.name}`);
        // S16: surface the new message in the Messages tab immediately.
        queryClient.invalidateQueries({
          queryKey: ["accountant-messages", target.id],
        });
      } else {
        // Bulk endpoint doesn't accept a per-send subject today — falls back to
        // the template default. If/when backend supports it we'll forward it.
        await apiPost("/api/accountant/users/bulk-remind", {
          client_ids: target.ids,
          template,
          custom_message: body,
        });
        toast.success(
          `Reminder sent to ${target.ids.length} client${target.ids.length === 1 ? "" : "s"}`,
        );
        // Invalidate messages for every client touched so individual detail
        // pages stay in sync if open.
        for (const id of target.ids) {
          queryClient.invalidateQueries({
            queryKey: ["accountant-messages", id],
          });
        }
      }
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Couldn't send reminder",
      );
    } finally {
      setSending(false);
    }
  };

  const toName =
    target.kind === "single"
      ? target.name
      : `${target.ids.length} selected clients`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl lg:max-w-6xl xl:max-w-7xl">
        <DialogHeader>
          <DialogTitle>Send reminder</DialogTitle>
          <DialogDescription>
            The reply-to is your accountant email, so clients can respond
            directly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Template</Label>
            <Select
              value={template}
              onValueChange={(v) =>
                setTemplate((v as ReminderTemplate) ?? "upload_receipts")
              }
            >
              <SelectTrigger className="w-full sm:w-60">
                <SelectValue>{REMINDER_TEMPLATE_LABELS[template]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {templates.map((k) => (
                  <SelectItem key={k} value={k}>
                    {REMINDER_TEMPLATE_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <EmailComposer
            key={template}
            toName={toName}
            defaultSubject={defaultSubject}
            initialBody=""
            // S14: rich text available for every template, not just custom.
            bodyMode="rich"
            bodyLabel={
              template === "custom" ? "Message" : "Additional note"
            }
            bodyRequired={template === "custom"}
            bodyPlaceholder={
              template === "custom"
                ? "Write your own message"
                : "Add extra context (optional)"
            }
            previewType="reminder"
            previewTemplate={template}
            clientId={target.kind === "single" ? target.id : undefined}
            sending={sending}
            onSend={handleSend}
            onCancel={() => onOpenChange(false)}
            sendLabel="Send reminder"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
